/**
 * Tempco Electric Heater Corporation - Material-UI Theme
 * Brand-compliant theme following Tempco Brand Book 2023 specifications
 * 
 * Colors from Tempco Brand Book:
 * - Tempco Red (Primary): #ED1C24 (Pantone 485 C)
 * - Tempco Red (Secondary): #AD1219 (Pantone 187C)
 * - Black: #000000 (Pantone Black C)
 * - White: #FFFFFF
 * - Dark Gray: #333333 (Pantone 179-15 C)
 * - Light Gray: #D9D9D6 (Pantone Cool Gray 1)
 * 
 * Typography:
 * - Headlines: Oswald (Website Headlines & Sub-Headlines)
 * - Body Text: Lato (Website Body Text)
 * - Alternative: Arial (Acceptable Alternative)
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    tempcoRed: {
      primary: string;
      secondary: string;
      light: string;
    };
  }

  interface PaletteOptions {
    tempcoRed?: {
      primary: string;
      secondary: string;
      light: string;
    };
  }
}

// Tempco Brand Colors
const tempcoColors = {
  // Primary Tempco Colors (from Brand Book)
  tempcoRedPrimary: '#ED1C24',     // Pantone 485 C
  tempcoRedSecondary: '#AD1219',   // Pantone 187C  
  tempcoRedLight: '#F05259',       // Lighter variant
  
  // Supporting Colors (from Brand Book)
  black: '#000000',                // Pantone Black C
  white: '#FFFFFF',
  darkGray: '#333333',             // Pantone 179-15 C
  lightGray: '#D9D9D6',            // Pantone Cool Gray 1
  
  // Functional Colors
  success: '#4caf50',
  warning: '#ff9800', 
  error: '#f44336',
  info: '#2196f3',
};

// Tempco Typography Settings
const tempcoTypography = {
  fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
  headlineFontFamily: '"Oswald", "Arial Black", "Arial", sans-serif',
  
  // Font weights
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
};

export const tempcoTheme = createTheme({
  palette: {
    mode: 'dark',
    
    // Primary color using Tempco Red
    primary: {
      main: tempcoColors.tempcoRedPrimary,
      light: tempcoColors.tempcoRedLight,
      dark: tempcoColors.tempcoRedSecondary,
      contrastText: tempcoColors.white,
    },
    
    // Secondary color using Tempco Red variants
    secondary: {
      main: tempcoColors.tempcoRedSecondary,
      light: tempcoColors.tempcoRedLight,
      dark: '#8B0F14',
      contrastText: tempcoColors.white,
    },
    
    // Custom Tempco color palette
    tempcoRed: {
      primary: tempcoColors.tempcoRedPrimary,
      secondary: tempcoColors.tempcoRedSecondary,
      light: tempcoColors.tempcoRedLight,
    },
    
    // Background colors with professional dark theme
    background: {
      default: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #333333 100%)',
      paper: 'rgba(51, 51, 51, 0.95)',
    },
    
    // Text colors following brand guidelines
    text: {
      primary: tempcoColors.white,
      secondary: tempcoColors.lightGray,
      disabled: 'rgba(255, 255, 255, 0.38)',
    },
    
    // Functional colors
    success: {
      main: tempcoColors.success,
      light: '#81c784',
      dark: '#388e3c',
      contrastText: tempcoColors.white,
    },
    
    warning: {
      main: tempcoColors.warning,
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: tempcoColors.white,
    },
    
    error: {
      main: tempcoColors.error,
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: tempcoColors.white,
    },
    
    info: {
      main: tempcoColors.info,
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: tempcoColors.white,
    },
    
    // Dividers and borders
    divider: 'rgba(255, 255, 255, 0.12)',
    
    // Action colors
    action: {
      active: tempcoColors.white,
      hover: 'rgba(237, 28, 36, 0.08)',
      selected: 'rgba(237, 28, 36, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.26)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  
  // Typography following Tempco Brand Book
  typography: {
    fontFamily: tempcoTypography.fontFamily,
    
    // Headlines using Oswald (Brand Book specification)
    h1: {
      fontFamily: tempcoTypography.headlineFontFamily,
      fontWeight: tempcoTypography.bold,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01562em',
      color: tempcoColors.white,
    },
    h2: {
      fontFamily: tempcoTypography.headlineFontFamily,
      fontWeight: tempcoTypography.semiBold,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.00833em',
      color: tempcoColors.white,
    },
    h3: {
      fontFamily: tempcoTypography.headlineFontFamily,
      fontWeight: tempcoTypography.semiBold,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      letterSpacing: '0em',
      color: tempcoColors.white,
    },
    h4: {
      fontFamily: tempcoTypography.headlineFontFamily,
      fontWeight: tempcoTypography.semiBold,
      fontSize: '1.5rem',
      lineHeight: 1.35,
      letterSpacing: '0.00735em',
      color: tempcoColors.white,
    },
    h5: {
      fontFamily: tempcoTypography.headlineFontFamily,
      fontWeight: tempcoTypography.medium,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '0em',
      color: tempcoColors.white,
    },
    h6: {
      fontFamily: tempcoTypography.headlineFontFamily,
      fontWeight: tempcoTypography.medium,
      fontSize: '1.125rem',
      lineHeight: 1.45,
      letterSpacing: '0.0075em',
      color: tempcoColors.white,
    },
    
    // Body text using Lato (Brand Book specification)
    body1: {
      fontFamily: tempcoTypography.fontFamily,
      fontWeight: tempcoTypography.regular,
      fontSize: '1rem',
      lineHeight: 1.6,
      color: tempcoColors.white,
    },
    body2: {
      fontFamily: tempcoTypography.fontFamily,
      fontWeight: tempcoTypography.regular,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      color: tempcoColors.lightGray,
    },
    
    // UI elements
    button: {
      fontFamily: tempcoTypography.fontFamily,
      fontWeight: tempcoTypography.semiBold,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      textTransform: 'none',
      letterSpacing: '0.02857em',
    },
    
    caption: {
      fontFamily: tempcoTypography.fontFamily,
      fontWeight: tempcoTypography.regular,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      color: tempcoColors.lightGray,
    },
    
    overline: {
      fontFamily: tempcoTypography.fontFamily,
      fontWeight: tempcoTypography.regular,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      textTransform: 'uppercase',
      letterSpacing: '0.08333em',
      color: tempcoColors.lightGray,
    },
    
    subtitle1: {
      fontFamily: tempcoTypography.fontFamily,
      fontWeight: tempcoTypography.regular,
      fontSize: '1rem',
      lineHeight: 1.75,
      color: tempcoColors.white,
    },
    
    subtitle2: {
      fontFamily: tempcoTypography.fontFamily,
      fontWeight: tempcoTypography.medium,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      color: tempcoColors.lightGray,
    },
  },
  
  // Shape and border radius
  shape: {
    borderRadius: 8, // Tempco standard border radius
  },
  
  // Spacing system (8px base unit)
  spacing: 8,
  
  // Transitions
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  
  // Component-specific overrides
  components: {
    // Global baseline styles
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #333333 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          fontFamily: tempcoTypography.fontFamily,
          color: tempcoColors.white,
        },
        
        // Ensure the root element and html also have the gradient background
        html: {
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #333333 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
        
        '#root': {
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #333333 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
        
        // Custom scrollbar with Tempco branding
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
        },
        '*::-webkit-scrollbar-thumb': {
          background: `rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.6)`,
          borderRadius: '8px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: tempcoColors.tempcoRedPrimary,
        },
        
        // Focus states for accessibility
        'button:focus-visible, input:focus-visible, [tabindex]:focus-visible': {
          outline: `2px solid ${tempcoColors.tempcoRedPrimary}`,
          outlineOffset: '2px',
        },
      },
    },
    
    // Card component with glass morphism effect
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(51, 51, 51, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.2)`,
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s ease-in-out',
          
          '&:hover': {
            borderColor: `rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.4)`,
            boxShadow: `0 12px 40px rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.1)`,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    
    // Button component with Tempco gradient
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '12px 24px',
          textTransform: 'none',
          fontWeight: tempcoTypography.semiBold,
          fontFamily: tempcoTypography.fontFamily,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          transition: 'all 0.2s ease-in-out',
          
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)',
          },
          
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        
        contained: {
          background: `linear-gradient(45deg, ${tempcoColors.tempcoRedPrimary} 30%, ${tempcoColors.tempcoRedLight} 90%)`,
          color: tempcoColors.white,
          
          '&:hover': {
            background: `linear-gradient(45deg, ${tempcoColors.tempcoRedSecondary} 30%, ${tempcoColors.tempcoRedPrimary} 90%)`,
          },
          
          '&:disabled': {
            background: 'rgba(51, 51, 51, 0.5)',
            color: 'rgba(255, 255, 255, 0.3)',
            transform: 'none',
            boxShadow: 'none',
          },
        },
        
        outlined: {
          borderColor: tempcoColors.tempcoRedPrimary,
          color: tempcoColors.tempcoRedPrimary,
          
          '&:hover': {
            borderColor: tempcoColors.tempcoRedPrimary,
            backgroundColor: `rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.08)`,
          },
        },
        
        text: {
          color: tempcoColors.tempcoRedPrimary,
          
          '&:hover': {
            backgroundColor: `rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.08)`,
          },
        },
      },
    },
    
    // TextField component with Tempco styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            fontFamily: tempcoTypography.fontFamily,
            
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
              transition: 'all 0.2s ease-in-out',
            },
            
            '&:hover fieldset': {
              borderColor: `rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.5)`,
            },
            
            '&.Mui-focused fieldset': {
              borderColor: tempcoColors.tempcoRedPrimary,
              borderWidth: '2px',
            },
          },
          
          '& .MuiInputLabel-root': {
            fontFamily: tempcoTypography.fontFamily,
            
            '&.Mui-focused': {
              color: tempcoColors.tempcoRedPrimary,
            },
          },
        },
      },
    },
    
    // AppBar with Tempco styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(51, 51, 51, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.2)`,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          boxShadow: `0 4px 20px rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.15)`,
        },
      },
    },
    
    // Chip component
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: tempcoTypography.fontFamily,
          fontWeight: tempcoTypography.medium,
        },
        
        filled: {
          backgroundColor: tempcoColors.tempcoRedPrimary,
          color: tempcoColors.white,
        },
        
        outlined: {
          borderColor: tempcoColors.tempcoRedPrimary,
          color: tempcoColors.tempcoRedPrimary,
        },
      },
    },
    
    // Alert component
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontFamily: tempcoTypography.fontFamily,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        },
        
        standardSuccess: {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          border: '1px solid rgba(76, 175, 80, 0.5)',
          color: '#81c784',
        },
        
        standardWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          border: '1px solid rgba(255, 152, 0, 0.5)',
          color: '#ffb74d',
        },
        
        standardError: {
          backgroundColor: `rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.15)`,
          border: `1px solid rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.5)`,
          color: tempcoColors.tempcoRedLight,
        },
        
        standardInfo: {
          backgroundColor: 'rgba(33, 150, 243, 0.15)',
          border: '1px solid rgba(33, 150, 243, 0.5)',
          color: '#64b5f6',
        },
      },
    },
    
    // Drawer component
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'rgba(51, 51, 51, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.2)`,
        },
      },
    },
    
    // Dialog component
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'rgba(51, 51, 51, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.2)`,
          borderRadius: '12px',
        },
      },
    },
    
    // Menu component
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: 'rgba(51, 51, 51, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.2)`,
          borderRadius: '8px',
        },
      },
    },
    
    // Tab component
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: tempcoTypography.fontFamily,
          fontWeight: tempcoTypography.medium,
          textTransform: 'none',
          
          '&.Mui-selected': {
            color: tempcoColors.tempcoRedPrimary,
          },
        },
      },
    },
    
    // Tooltip component
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(51, 51, 51, 0.95)',
          border: `1px solid rgba(${tempcoColors.tempcoRedPrimary.substring(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.2)`,
          fontFamily: tempcoTypography.fontFamily,
        },
      },
    },
  },
} as ThemeOptions);

export default tempcoTheme;
